import { appSettings } from '../context'
import { setHeaderHeight } from '../utils'

class HeaderVisibility {
	private root: HTMLElement | null = null
	private inner: HTMLElement | null = null
	private scroller: HTMLElement | null = null
	private lastScrollTop = 0
	private isCondensed = false
	private readonly threshold = 8
	private readonly activationOffset = 180

	init(container: Document | HTMLElement = document) {
		this.root = container.querySelector<HTMLElement>('.hg-header') || document.querySelector<HTMLElement>('.hg-header')
		this.inner = this.root?.querySelector<HTMLElement>('.hg-header__inner') || null
		this.scroller = appSettings.scroller as HTMLElement | null
		this.lastScrollTop = this.getScrollTop()
		this.isCondensed = !!this.root?.classList.contains('is-condensed')

		this.refresh()

		document.addEventListener('hg:scroll', this.onSmoothScroll as EventListener)
		this.scroller?.addEventListener('scroll', this.onNativeScroll, { passive: true })
	}

	update(container: Document | HTMLElement = document) {
		this.cleanup()
		this.init(container)
	}

	cleanup() {
		document.removeEventListener('hg:scroll', this.onSmoothScroll as EventListener)
		this.scroller?.removeEventListener('scroll', this.onNativeScroll)
		gsap.killTweensOf(this.root)
	}

	refresh() {
		setHeaderHeight()
		this.setAnimationState(this.isCondensed)
	}

	private onSmoothScroll = (e: CustomEvent) => {
		const scrollTop = typeof e.detail?.scroll === 'number' ? e.detail.scroll : this.getScrollTop()
		this.handleScroll(scrollTop)
	}

	private onNativeScroll = () => {
		this.handleScroll(this.getScrollTop())
	}

	private handleScroll(scrollTop: number) {
		if (!this.root) return

		if (scrollTop <= this.activationOffset) {
			this.setCondensed(false)
			this.lastScrollTop = scrollTop
			return
		}

		const delta = scrollTop - this.lastScrollTop
		if (Math.abs(delta) < this.threshold) return

		this.setCondensed(delta > 0)
		this.lastScrollTop = scrollTop
	}

	private setCondensed(nextValue: boolean) {
		if (!this.root || this.isCondensed === nextValue) return

		this.isCondensed = nextValue
		this.root.classList.toggle('is-condensed', nextValue)
		this.animate(nextValue)
	}

	private getScrollTop(): number {
		return this.scroller?.scrollTop || 0
	}

	private animate(isCondensed: boolean) {
		if (!this.root) return

		const offset = isCondensed ? this.getInnerHeight() : 0
		const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 0.36

		gsap.killTweensOf(this.root)
		gsap.to(this.root, {
			y: -offset,
			duration,
			ease: 'sine.out',
		})
	}

	private setAnimationState(isCondensed: boolean) {
		const offset = isCondensed ? this.getInnerHeight() : 0

		gsap.set(this.root, { y: -offset })
	}

	private getInnerHeight(): number {
		return this.inner?.offsetHeight || 0
	}
}

export default new HeaderVisibility()
